import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Eye, Users, Scale } from "lucide-react";

const PORTRAIT = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/seozx7an_ChatGPT%20Image%20Mar%2026%2C%202026%2C%2002_10_01%20PM.png";
const VEGAS = "https://static.prod-images.emergentagent.com/jobs/02c52b1a-551f-4ab4-bb4f-fdecb1df21b7/images/0f0117b2a3073ea1f593ae4efb3f6d3a225593a990bab5873031e638cc2824ed.png";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/2vawzd5m_C20B16E2-44DA-482E-8924-F5E084C6A7C8_4_5005_c.jpeg";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" } })
};

const GOVERNANCE = [
  { icon: Shield, title: "Independent Board", desc: "Majority independent board ensuring unbiased decision-making and fiduciary responsibility." },
  { icon: Eye, title: "Full Transparency", desc: "Open financial reporting, regular audits, and clear communication with all stakeholders." },
  { icon: Users, title: "Community First", desc: "Every decision is measured against its impact on the veteran community we serve." },
  { icon: Scale, title: "Ethical Standards", desc: "Strict ethical guidelines governing all operations, partnerships, and content creation." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* ── HERO ── */}
      <section className="pt-32 pb-24 lg:pt-40 lg:pb-32 relative noise-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">About</p>
          </motion.div>
          <motion.h1
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] uppercase tracking-tighter leading-[0.9] mb-6 max-w-4xl"
          >
            We Are<br/>Bad Company
          </motion.h1>
          <motion.p
            initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="font-body text-base lg:text-lg text-zinc-400 max-w-2xl leading-relaxed"
          >
            A veteran-focused content and experience platform that combines storytelling, 
            entertainment, and real-world impact. We exist to honor those who served.
          </motion.p>
        </div>
      </section>

      {/* ── WHO WE ARE ── */}
      <section className="py-24 lg:py-32 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="overflow-hidden border border-white/10">
                <img src={PORTRAIT} alt="Veteran" className="w-full aspect-[4/5] object-cover" />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Who We Are</p>
              <h2 className="font-subheading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-tight mb-6">
                Built By Veterans,<br/>For Veterans
              </h2>
              <p className="font-body text-base lg:text-lg text-zinc-400 leading-relaxed mb-6">
                Bad Company was founded on a simple belief: the men and women who served this country 
                deserve more than a thank you. They deserve experiences that reignite their sense of 
                purpose, adventure, and brotherhood.
              </p>
              <p className="font-body text-base text-zinc-500 leading-relaxed">
                Based in Las Vegas, we leverage the energy and excitement of the entertainment capital 
                to create unforgettable moments. From high-adrenaline adventures to intimate gatherings, 
                every experience is designed with intention and respect.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── WHY WE STARTED ── */}
      <section className="py-24 lg:py-32 relative">
        <div className="absolute inset-0">
          <img src={VEGAS} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-black/80" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Our Origin</p>
            <h2 className="font-subheading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-tight mb-8">
              Why We Started
            </h2>
            <p className="font-body text-base lg:text-lg text-zinc-400 leading-relaxed mb-6 max-w-2xl mx-auto">
              Too many veterans return home to a world that doesn't understand what they've been through. 
              The transition from service to civilian life is one of the hardest battles they'll face.
            </p>
            <p className="font-body text-base text-zinc-500 leading-relaxed max-w-2xl mx-auto">
              Bad Company was born from the understanding that shared experiences heal. That storytelling 
              bridges gaps. That when you show the world who these veterans really are - their strength, 
              their humor, their humanity - everything changes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT MAKES US DIFFERENT ── */}
      <section className="py-24 lg:py-32 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">The Difference</p>
            <h2 className="font-subheading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight">What Sets Us Apart</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                num: "01",
                title: "Real Experiences",
                desc: "We don't just talk about supporting veterans. We create tangible, life-changing experiences in Las Vegas and beyond."
              },
              {
                num: "02",
                title: "Authentic Storytelling",
                desc: "Our content captures raw, unfiltered stories. No scripts, no corporate polish - just real veterans sharing real moments."
              },
              {
                num: "03",
                title: "Lasting Impact",
                desc: "Every experience is documented, shared, and amplified. The impact extends far beyond the moment, inspiring audiences worldwide."
              }
            ].map((item, i) => (
              <motion.div
                key={item.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                className="border border-white/10 p-8 card-hover"
              >
                <span className="font-heading text-5xl text-white/5">{item.num}</span>
                <h3 className="font-subheading text-xl uppercase tracking-normal mt-4 mb-3">{item.title}</h3>
                <p className="font-body text-sm text-zinc-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GOVERNANCE ── */}
      <section data-testid="governance-section" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Integrity</p>
            <h2 className="font-subheading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-4">
              Governance & Integrity
            </h2>
            <p className="font-body text-base text-zinc-500 max-w-xl mx-auto">
              Trust is earned. We hold ourselves to the highest standards of transparency and accountability.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {GOVERNANCE.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="border border-white/10 p-6 card-hover text-center"
              >
                <div className="w-12 h-12 border border-white/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon size={20} className="text-zinc-400" />
                </div>
                <h3 className="font-subheading text-sm uppercase tracking-wider mb-2">{item.title}</h3>
                <p className="font-body text-xs text-zinc-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 lg:py-32 bg-zinc-950 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <img src={LOGO_URL} alt="Bad Company" className="h-20 w-20 object-contain mx-auto mb-8" />
          <h2 className="font-heading text-4xl sm:text-5xl uppercase tracking-tighter leading-[0.9] mb-6">
            Join The Mission
          </h2>
          <Link
            to="/get-involved"
            data-testid="about-cta-get-involved"
            className="inline-flex items-center gap-2 font-subheading text-sm uppercase tracking-widest bg-white text-black px-8 py-3.5 hover:bg-zinc-200 transition-colors duration-300"
          >
            Get Involved <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
